import React from "react";
import PropTypes from "prop-types";

// Utils
import { saveAs } from "file-saver";

// Material
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";

// Config + Utils
import { getFileExtension } from "../../utils/images";
import { getPlaceholder } from "../../config/values";
import useTranslate from "../../hooks/useTranslate";

// Component
import Button from "../Button";
import ContentDelay from "./ContentDelay";
import DisplayImage from "./DisplayImage";
import Text from "../Text/Text";
import VideoPlayer from "./VideoPlayer";

const useStyles = makeStyles(theme => ({
  placeholder: {
    width: 600,
    height: "70vh",
    padding: 40,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center"
  },
  noPreview: {
    margin: theme.spacing(4, 0),
    textAlign: "center"
  },
  image: {
    maxWidth: "100%",
    maxHeight: "70vh",
    display: "block",
    minHeight: 200
  },
  embed: {
    width: "70vw",
    height: "calc(70vh - 55px)",
    border: 0
  },
  galleryItem: {
    minHeight: 200,
    minWidth: 200,
    maxWidth: "70vw",
    display: "flex",
    flexDirection: "column",

    "& .pg-viewer-wrapper": {
      overflow: "initial"
    }
  },
  downloadButton: {
    height: 50,
    width: 200
  },
  floatingButton: {
    width: "100%",
    backgroundColor: "rgba(0,0,0,0.7)",
    padding: theme.spacing(1, 3),
    display: "flex",
    justifyContent: "flex-end",
    position: "sticky",
    top: 0,
    zIndex: 99
  },
  downloadIconButton: {
    filter: "brightness(5)"
  }
}));

const handleDownload = (src, hls, dash, filename) => async () => {
  const response = await fetch(src);
  const data = await response.blob();
  saveAs(data, filename);
};

// getImage (the retry's re-fetch) has no failure callback - if it silently
// no-ops, or comes back with the same URL, src never changes on its own.
const AUTH_RETRY_TIMEOUT_MS = 8000;

const MediaItemImage = ({ filename, src, hls, dash, type, onAuthError }) => {
  const classes = useStyles();
  const [showPlaceholder, setShowPlaceholder] = React.useState(false);
  const [retrying, setRetrying] = React.useState(false);
  const getText = useTranslate();

  // Caps the auto-retry to once per image, regardless of how many distinct
  // URLs it goes through - avoids looping if a refreshed URL still 403s for
  // an unrelated reason. The caller keys this component by filename, so a
  // new image always means a fresh mount (and a fresh ref) - no manual
  // reset needed here.
  const attemptedRetry = React.useRef(false);

  // handleNonImage's status probe is async - avoids updating state if the
  // gallery has since navigated away (unmounting this instance) before it
  // resolves.
  const isMounted = React.useRef(true);
  React.useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  React.useEffect(() => {
    const hasSrc = src || (hls && dash);
    if (!hasSrc) setShowPlaceholder(true);
    else setShowPlaceholder(false);
    setRetrying(false);
  }, [src]);

  React.useEffect(() => {
    if (!retrying) return undefined;
    const timeoutId = setTimeout(() => {
      setRetrying(false);
      setShowPlaceholder(true);
    }, AUTH_RETRY_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [retrying]);

  const handleNonImage = async () => {
    if (onAuthError && src && !attemptedRetry.current) {
      attemptedRetry.current = true;
      const response = await fetch(src).catch(() => null);
      if (!isMounted.current) return;
      if (response?.status === 403) {
        setRetrying(true);
        onAuthError();
        return;
      }
    }
    if (isMounted.current) setShowPlaceholder(true);
  };

  const previews = React.useMemo(
    () => ({
      document: s => <embed src={s.src} className={classes.embed} />,
      video: s => (
        <ContentDelay
          render={handleRetry => (
            <VideoPlayer
              className={classes.image}
              handleNonImage={handleNonImage}
              hls={s.hls}
              dash={s.dash}
              onError={handleRetry}
            />
          )}
        />
      ),
      image: s => (
        <DisplayImage
          alt=""
          className={classes.image}
          onError={handleNonImage}
          src={s.src}
        />
      )
    }),
    [classes]
  );

  const extension = getFileExtension(filename);
  const component = previews[type] || previews[extension];

  if (retrying) {
    return (
      <div className={classes.placeholder}>
        <CircularProgress />
      </div>
    );
  }

  return !showPlaceholder && component ? (
    <div className={classes.galleryItem}>{component({ src, hls, dash })}</div>
  ) : (
    <div className={classes.placeholder}>
      <img src={getPlaceholder(filename)} alt="" />
      <Text
        className={classes.noPreview}
        text="mediaGalleryPage.noPreview"
        type="valueLabel"
      />
      <Button
        className={classes.downloadButton}
        color="primary"
        disableElevation
        onClick={handleDownload(src, filename)}
        variant="contained"
      >
        {getText("mediaGalleryPage.downloadButton")}
      </Button>
    </div>
  );
};

MediaItemImage.propTypes = {
  filename: PropTypes.string.isRequired,
  dash: PropTypes.string,
  hls: PropTypes.string,
  src: PropTypes.string,
  type: PropTypes.string.isRequired,
  // Called on a 403 (expired signed URL) so the caller can re-fetch and
  // pass back a fresh src. No retry attempted when omitted.
  onAuthError: PropTypes.func
};

MediaItemImage.defaultProps = {
  dash: null,
  hls: null,
  src: null,
  onAuthError: null
};

export default React.memo(MediaItemImage, (prevProps, nextProps) => {
  return (
    prevProps.filename === nextProps.filename &&
    prevProps.src === nextProps.src &&
    prevProps.hls === nextProps.hls &&
    prevProps.dash === nextProps.dash &&
    prevProps.type === nextProps.type
  );
});
